import { memo, useState } from 'react'
import { useTranslation } from '@ufc/core'
import {
  Edit3,
  Eye,
  Trash2,
  MoreHorizontal,
  cn,
  Button,
  TableCell,
  TableRow,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  TenantCell,
} from '@ufc/uikit'
import type { AddressRowProps } from '../types'

function AddressRowComponent({
  address,
  isSelected,
  showTenantColumn,
  onClick,
  onDoubleClick,
  onEdit,
  onDelete,
}: AddressRowProps) {
  const { t } = useTranslation()
  const [dropdownOpen, setDropdownOpen] = useState(false)

  return (
    <TableRow
      className={cn(
        'border-border/30 cursor-pointer group transition-colors duration-150',
        isSelected
          ? 'bg-primary/8 hover:bg-primary/12 ring-1 ring-inset ring-primary/20'
          : 'hover:bg-muted/50'
      )}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <TableCell className="px-5 py-4">
        <span className="font-semibold text-foreground text-sm">
          {address.name}
        </span>
      </TableCell>
      {showTenantColumn && (
        <TableCell className="px-5 py-4">
          <TenantCell tenant={address.tenant} />
        </TableCell>
      )}
      <TableCell className="px-5 py-4 max-w-[200px]">
        <span className="text-muted-foreground text-sm truncate block">
          {address.address}
        </span>
      </TableCell>
      <TableCell className="px-5 py-4">
        <span className="text-muted-foreground text-sm">{address.city}</span>
      </TableCell>
      <TableCell className="px-5 py-4 text-end">
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-xl hover:bg-muted"
            >
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end" 
            onCloseAutoFocus={(e) => e.preventDefault()}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault()
                e.stopPropagation()
                // Close dropdown first
                setDropdownOpen(false)
                // Wait for dropdown to close, then open drawer
                setTimeout(() => {
                  onClick()
                }, 150)
              }}
            >
              <Eye className="w-4 h-4 me-2" />
              {t('common.view')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault()
                e.stopPropagation()
                // Close dropdown first
                setDropdownOpen(false)
                // Wait for dropdown to close, then open drawer
                setTimeout(() => {
                  onEdit?.(address)
                }, 150)
              }}
            >
              <Edit3 className="w-4 h-4 me-2" />
              {t('common.edit')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onClick={(e) => {
                e.stopPropagation()
                onDelete?.(address)
              }}
            >
              <Trash2 className="w-4 h-4 me-2" />
              {t('common.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  )
}

export const AddressRow = memo(AddressRowComponent, (prev, next) => {
  return (
    prev.address.id === next.address.id &&
    prev.address.name === next.address.name &&
    prev.address.address === next.address.address &&
    prev.address.city === next.address.city &&
    prev.isSelected === next.isSelected &&
    prev.showTenantColumn === next.showTenantColumn
  )
})
